import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { UserConfigService, UserConfigurationResponse } from '../utils/apiService';
import { useAuth, AuthState } from './AuthContext';

// Define possible configuration states
export enum ConfigState {
  INITIALIZING = 'initializing',
  CHECKING = 'checking',
  CONFIGURED = 'configured',
  UNCONFIGURED = 'unconfigured',
  ERROR = 'error'
}

interface ConfigContextData {
  configurations: UserConfigurationResponse[];
  isConfigLoading: boolean;
  hasConfigurations: boolean;
  isFirstTimeSetup: boolean;
  configError: string | null;
  configChecked: boolean;
}

interface ConfigContextType extends ConfigContextData {
  configState: ConfigState;
  loadConfigurations: (forceRefresh?: boolean) => Promise<UserConfigurationResponse[]>;
  setConfigChecked: (checked: boolean) => void;
  markFirstTimeSetup: (isFirst: boolean) => void;
  resetConfigState: () => void;
}

const initialState: ConfigContextData = {
  configurations: [],
  isConfigLoading: false,
  hasConfigurations: false,
  isFirstTimeSetup: false,
  configError: null,
  configChecked: false
};

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const [data, setData] = useState<ConfigContextData>(initialState);
  const [configState, setConfigState] = useState<ConfigState>(ConfigState.INITIALIZING);
  const { authState } = useAuth();
  
  // Add a ref to track ongoing config loading and implement caching
  const configLoadPromiseRef = useRef<Promise<UserConfigurationResponse[]> | null>(null);
  const lastConfigLoadRef = useRef<number>(0);

  // Debug logging for config context (only in development)
  const logConfigInfo = (message: string, data?: any) => {
    // Enable debug logging temporarily to troubleshoot
    if (import.meta.env.DEV) {
      if (data) {
        console.debug(`[Config] ${message}`, data);
      } else {
        console.debug(`[Config] ${message}`);
      }
    }
  };

  // Load configuration from localStorage on mount
  useEffect(() => {
    const isFirstLogin = localStorage.getItem('kms_first_login') === 'true';
    
    if (isFirstLogin) {
      setData(prev => ({
        ...prev,
        isFirstTimeSetup: true
      }));
    }
    
    logConfigInfo('Initial state', { configState, isFirstLogin });
  }, []);

  // Sync with authentication state changes
  useEffect(() => {
    logConfigInfo('Auth or config status changed', { authState, configChecked: data.configChecked, configState });

    if (authState === AuthState.AUTHENTICATED && !data.configChecked && configState !== ConfigState.CHECKING) {
      logConfigInfo('Authenticated and config not checked, loading configurations.');
      loadConfigurations();
    } else if (authState === AuthState.UNAUTHENTICATED && configState !== ConfigState.INITIALIZING) {
      logConfigInfo('Not authenticated, resetting config state.');
      resetConfigState();
    }
  }, [authState, data.configChecked, configState]);

  const loadConfigurations = async (forceRefresh: boolean = false): Promise<UserConfigurationResponse[]> => {
    logConfigInfo('Loading user configurations...');
    
    // If there's already an ongoing config load and we're not forcing refresh, return that promise
    if (configLoadPromiseRef.current && !forceRefresh) {
      logConfigInfo('Reusing in-progress config loading');
      return configLoadPromiseRef.current;
    }
    
    // Clear existing promise if forcing refresh
    if (forceRefresh) {
      configLoadPromiseRef.current = null;
      // Also clear any conflicting cache when force refreshing
      localStorage.removeItem('last_config_check_time');
    }
    
    // Update last load time to prevent excessive calls
    lastConfigLoadRef.current = Date.now();
    
    // Create and store the config loading promise
    const loadPromise = (async () => {
      try {
        setData(prev => ({
          ...prev,
          isConfigLoading: true,
          configError: null
        }));
        
        setConfigState(ConfigState.CHECKING);
        
        // Log localStorage state before loading
        try {
          const configCount = localStorage.getItem('kms_config_count');
          const activeConfigId = localStorage.getItem('kms_active_config_id');
          logConfigInfo('LocalStorage before loading', { configCount, activeConfigId, forceRefresh });
        } catch (e) {
          // Non-critical, just log the error
          console.error('Error reading localStorage', e);
        }
        
        // Load configurations from API
        let configs: UserConfigurationResponse[] = [];
        
        try {
          configs = await UserConfigService.getConfigurations();
          logConfigInfo(`Successfully loaded ${configs.length} configurations from API`);
          
          // If this is a force refresh and we expect configurations but got none,
          // add a small delay and retry once
          if (forceRefresh && configs.length === 0) {
            const expectedCount = parseInt(localStorage.getItem('kms_config_count') || '0');
            if (expectedCount > 0) {
              logConfigInfo('Force refresh returned no configs but localStorage indicates configs should exist. Retrying...');
              await new Promise(resolve => setTimeout(resolve, 500));
              configs = await UserConfigService.getConfigurations();
              logConfigInfo(`Retry loaded ${configs.length} configurations`);
            }
          }
        } catch (error) {
          logConfigInfo('Error loading configurations:', error);
          throw error; // Re-throw to handle in outer catch
        }
        
        // Set the state with the results
        const newData = {
          configurations: configs,
          hasConfigurations: configs.length > 0,
          isConfigLoading: false,
          configChecked: true,
          isFirstTimeSetup: configs.length === 0
        };
        
        setData(prev => ({
          ...prev,
          ...newData
        }));
        
        // Update config state based on results
        const newConfigState = configs.length > 0 ? ConfigState.CONFIGURED : ConfigState.UNCONFIGURED;
        setConfigState(newConfigState);
        
        // Debug logging for state changes
        console.log(`[ConfigContext] State updated:`, {
          configCount: configs.length,
          newConfigState,
          hasConfigurations: configs.length > 0,
          configChecked: true,
          isConfigLoading: false,
          isFirstTimeSetup: configs.length === 0,
          forceRefresh
        });
        
        logConfigInfo(`Configuration loading complete. State: ${newConfigState}, Count: ${configs.length}`);
        
        // Update localStorage based on actual results
        if (configs.length === 0) {
          localStorage.setItem('kms_first_login', 'true');
          localStorage.removeItem('kms_config_count');
          localStorage.removeItem('kms_active_config_id');
          localStorage.removeItem('kms_active_config_name');
          localStorage.removeItem('last_config_check_time');
          logConfigInfo('No configurations found, updated localStorage accordingly');
        } else {
          localStorage.removeItem('kms_first_login');
          localStorage.setItem('kms_config_count', configs.length.toString());
          localStorage.setItem('last_config_check_time', Date.now().toString());
          
          // Use the first configuration as the active one
          const defaultConfig = configs[0];
          localStorage.setItem('kms_active_config_id', defaultConfig.config_id);
          localStorage.setItem('kms_active_config_name', defaultConfig.display_name);
          
          // Store user email from config if available
          if (defaultConfig.user_email) {
            try {
              localStorage.setItem('kms_user_email', defaultConfig.user_email);
              logConfigInfo(`Stored user email from config: ${defaultConfig.user_email}`);
            } catch (e) {
              logConfigInfo('Error storing user email in localStorage:', e);
            }
          }
          
          logConfigInfo(`Updated localStorage with ${configs.length} configurations, default: ${defaultConfig.display_name}`);
        }
        
        // Additional debug logging after all updates
        console.log('[ConfigContext] All updates completed, final state:', {
          configState: newConfigState,
          hasConfigurations: configs.length > 0,
          configCount: configs.length
        });
        
        return configs;
      } catch (error) {
        logConfigInfo('Error loading configurations:', error);
        
        // Before setting error state, check if we have localStorage data
        // that indicates configurations should exist
        const storedConfigCount = parseInt(localStorage.getItem('kms_config_count') || '0');
        const hasLocalStorageConfigs = storedConfigCount > 0;
        
        setData(prev => ({
          ...prev,
          configError: error instanceof Error ? error.message : 'Failed to load configurations',
          isConfigLoading: false,
          configChecked: true, // Mark as checked to prevent infinite retry loops
          // Only set as first time setup if we have no localStorage data
          isFirstTimeSetup: !hasLocalStorageConfigs
        }));
        
        // Only change config state to ERROR if we don't have localStorage data
        // Otherwise, keep the previous state to avoid disrupting the user experience
        if (!hasLocalStorageConfigs) {
          setConfigState(ConfigState.ERROR);
          // Set first time setup flag on error if no localStorage data
          localStorage.setItem('kms_first_login', 'true');
        } else {
          // If we have localStorage data, assume CONFIGURED for now
          setConfigState(ConfigState.CONFIGURED);
        }
        
        return [];
      } finally {
        // Clear the promise reference when done
        configLoadPromiseRef.current = null;
      }
    })();
    
    // Store the promise so concurrent calls can use it
    configLoadPromiseRef.current = loadPromise;
    return loadPromise;
  };

  const setConfigChecked = (checked: boolean) => {
    logConfigInfo(`Setting configChecked to ${checked}`);
    setData(prev => ({
      ...prev,
      configChecked: checked
    }));
  };

  const markFirstTimeSetup = (isFirst: boolean) => {
    console.log(`[ConfigContext] markFirstTimeSetup called with: ${isFirst}`);
    setData(prev => ({
      ...prev,
      isFirstTimeSetup: isFirst
    }));
    
    if (isFirst) {
      localStorage.setItem('kms_first_login', 'true');
      setConfigState(ConfigState.UNCONFIGURED);
      console.log('[ConfigContext] Marked as first time setup - state: UNCONFIGURED');
    } else {
      localStorage.removeItem('kms_first_login');
      // Don't immediately set to CONFIGURED here - let loadConfigurations handle it
      console.log('[ConfigContext] Marked as NOT first time setup - will check configurations');
    }
  };

  const resetConfigState = () => {
    logConfigInfo('Resetting config state');
    setData(initialState);
    setConfigState(ConfigState.INITIALIZING);
  };

  return (
    <ConfigContext.Provider 
      value={{
        ...data,
        configState,
        loadConfigurations,
        setConfigChecked,
        markFirstTimeSetup,
        resetConfigState
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
} 